import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import InputField, { INPUT_TYPE_SELECT } from 'components/InputField';
import Button from 'components/Button';
import { isValidEmail } from 'utils/form';
import { getLocaleMap } from 'utils/locales';
import { splitPhoneNumber } from 'utils/phone';
import { useNavigate } from 'react-router-dom';
import Select from 'components/Select';
import phoneOptionsTemplate from 'components/Select/phone_options.json';
import map from 'lodash/map';
import mapFp from 'lodash/fp/map';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import split from 'lodash/split';

const mapValue = mapFp('value');

const EditUserForm = ({ user, roles, onSave,
  countries,
  fedexAccounts,
  iWatchAccounts,
  setPasswordButtonColor,
  setPasswordFontColor,
  saveButtonColor,
  saveButtonFontColor,
  backButtonColor,
  backButtonFontColor,
  ...props
}) => {
  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap([
      'labels_display_name',
      'labels_email',
      'labels_password',
      'labels_confirm_password',
      'labels_role',
      'labels_phone',
      'errors_required',
      'errors_invalid_email',
      'errors_password_not_match',
      'set_password_button',
      'save_button',
      'Admin',
    ])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const getFormValidation = (_user) => (values) => {
    const { display_name, email, role, password, confirm_password } = values;
    const errors = {};
    if (!display_name) {
      errors.display_name = lm.errors_required;
    }
    if (!email) {
      errors.email = lm.errors_required;
    } else if (!isValidEmail(email)) {
      errors.email = lm.errors_invalid_email;
    }
    if (!role) {
      errors.role = lm.errors_required;
    }
    if (!_user) {
      if (!password) {
        errors.password = lm.errors_required;
      }
      if (!confirm_password) {
        errors.confirm_password = lm.errors_required;
      }
      if (password && confirm_password && password !== confirm_password) {
        errors.confirm_password = lm.errors_password_not_match;
        errors.password = lm.errors_password_not_match;
      }
    }
    return errors;
  };

  const navigate = useNavigate();

  const initialValues = useMemo(() => {
    // const userCountries = user?.user_countries || [];
    // const userCountryIds = map(userCountries, ['country', 'country_id']);
    const userFedexAccounts = user?.user_fedex_accounts || [];
    const userFedexAccountIds = map(userFedexAccounts, ['fedex_account', 'fedex_account_id']);
    const userIWatchAccounts = user?.user_iwatch_accounts || [];
    const userIWatchAccountIds = map(userIWatchAccounts, ['iwatch_account', 'iwatch_account_id']);
    const countryList = user?.country_list || '';
    const selectedCountries = countryList.split(',');
    return {
      display_name: user?.display_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      role: user?.role || 2,
      ...(user ? {} : {
        password: '',
        confirm_password: '',
      }),
      country_list: countryList,
      selected_countries: selectedCountries,
      // user_countries: userCountries,
      // initial_countries: userCountryIds,
      // selected_countries: clone(userCountryIds),
      user_fedex_accounts: userFedexAccounts,
      initial_fedex_accounts: userFedexAccountIds,
      selected_fedex_accounts: clone(userFedexAccountIds),
      user_iwatch_accounts: userIWatchAccounts,
      initial_iwatch_accounts: userIWatchAccountIds,
      selected_iwatch_accounts: clone(userIWatchAccountIds),
    };
  }, [user]);

  const roleOptions = useMemo(() => roles.map((role) => ({
    label: role.description,
    // label: lm[role.description],
    value: role.role_id,
  })), [roles]);

  const [phoneOptions, setPhoneOptions] = useState(cloneDeep(phoneOptionsTemplate));

  const countriesOptions = useMemo(() => countries.map((country) => ({
    label: country.name,
    // value: country.country_id,
    value: country.name,
  })), [countries]);

  const [selectedCountries, setSelectedCountrires] = useState([]);

  const handleChangeCountry = useCallback((values, setFieldValue) => {
    const added = values.filter((item) => {
      return selectedCountries.indexOf(item) === -1;
    });
    const removed = selectedCountries.filter((item) => {
      return values.indexOf(item) === -1;
    });

    if (added.includes('all')) {
      const ids = mapValue(countriesOptions);
      setFieldValue('selected_countries', ['all', ...ids]);
      setSelectedCountrires(['all', ...ids]);
    } else if (removed.includes('all')) {
      setFieldValue('selected_countries', []);
      setSelectedCountrires([]);
    } else if (removed.length > 0) {
      const indexOfAll = values.indexOf('all');
      if (indexOfAll > -1) { // only splice array when item is found
        values.splice(indexOfAll, 1);
      }
      setFieldValue('selected_countries', values);
      setSelectedCountrires(clone(values));
    } else {
      let newValues;
      const ids = values.filter(value => value !== 'all');
      if (ids.length === countries.length) {
        newValues = ['all', ...ids];
      } else {
        newValues = values;
      }
      setFieldValue('selected_countries', newValues);
      setSelectedCountrires(clone(newValues));
    }
  }, [countries.length, countriesOptions, selectedCountries]);

  const fedexAccountOptions = useMemo(() => fedexAccounts.map((fedexAccount) => ({
    label: `${fedexAccount.key} (${fedexAccount.username})`,
    value: fedexAccount.fedex_account_id,
  })), [fedexAccounts]);

  const [selectedFedexAccounts, setSelectedFedexAccounts] = useState([]);

  const handleChangeFedexAccount = useCallback((values, setFieldValue) => {
    const added = values.filter((item) => {
      return selectedFedexAccounts.indexOf(item) === -1;
    });
    const removed = selectedFedexAccounts.filter((item) => {
      return values.indexOf(item) === -1;
    });

    if (added.includes('all')) {
      const ids = mapValue(fedexAccountOptions);
      setFieldValue('selected_fedex_accounts', ['all', ...ids]);
      setSelectedFedexAccounts(['all', ...ids]);
    } else if (removed.includes('all')) {
      setFieldValue('selected_fedex_accounts', []);
      setSelectedFedexAccounts([]);
    } else if (removed.length > 0) {
      const indexOfAll = values.indexOf('all');
      if (indexOfAll > -1) { // only splice array when item is found
        values.splice(indexOfAll, 1);
      }
      setFieldValue('selected_fedex_accounts', values);
      setSelectedFedexAccounts(values);
    } else {
      let newValues;
      const ids = values.filter(value => value !== 'all');
      if (ids.length === fedexAccounts.length) {
        newValues = ['all', ...ids];
      } else {
        newValues = values;
      }
      setFieldValue('selected_fedex_accounts', newValues);
      setSelectedFedexAccounts(clone(newValues));
    }
  }, [fedexAccounts.length, fedexAccountOptions, selectedFedexAccounts]);

  const iWatchAccountOptions = useMemo(() => iWatchAccounts.map((iWatchAccount) => ({
    label: `${iWatchAccount.key} (${iWatchAccount.name}) (${iWatchAccount.phone})`,
    value: iWatchAccount.iwatch_account_id,
  })), [iWatchAccounts]);

  const [selectedIWatchAccounts, setSelectedIWatchAccounts] = useState([]);

  const handleChangeIWatchAccount = useCallback((values, setFieldValue) => {
    const added = values.filter((item) => {
      return selectedIWatchAccounts.indexOf(item) === -1;
    });
    const removed = selectedIWatchAccounts.filter((item) => {
      return values.indexOf(item) === -1;
    });

    if (added.includes('all')) {
      const ids = mapValue(iWatchAccountOptions);
      setFieldValue('selected_iwatch_accounts', ['all', ...ids]);
      setSelectedIWatchAccounts(['all', ...ids]);
    } else if (removed.includes('all')) {
      setFieldValue('selected_iwatch_accounts', []);
      setSelectedIWatchAccounts([]);
    } else if (removed.length > 0) {
      const indexOfAll = values.indexOf('all');
      if (indexOfAll > -1) { // only splice array when item is found
        values.splice(indexOfAll, 1);
      }
      setFieldValue('selected_iwatch_accounts', values);
      setSelectedIWatchAccounts(values);
    } else {
      let newValues;
      const ids = values.filter(value => value !== 'all');
      if (ids.length === iWatchAccounts.length) {
        newValues = ['all', ...ids];
      } else {
        newValues = values;
      }
      setFieldValue('selected_iwatch_accounts', newValues);
      setSelectedIWatchAccounts(clone(newValues));
    }
  }, [iWatchAccounts.length, iWatchAccountOptions, selectedIWatchAccounts]);

  const formikRef = useRef();
  useEffect(() => {
    const countryListArray = split(initialValues.country_list, ',');
    if (countryListArray.length === 1 && countryListArray[0] === '') {
      countryListArray.pop();
    }
    if (formikRef.current && countryListArray.length > 0 && countries.length > 0) {
      if ((countryListArray.length === countries.length)) {
        formikRef.current.setFieldValue('selected_countries', ['all', ...countryListArray]);
        setSelectedCountrires(['all', ...countryListArray]);
      }
    }
  }, [countries, initialValues]);
  useEffect(() => {
    if (formikRef.current && initialValues.user_fedex_accounts.length > 0 && fedexAccounts.length > 0) {
      if ((initialValues.user_fedex_accounts.length === fedexAccounts.length)) {
        formikRef.current.setFieldValue('selected_fedex_accounts', ['all', ...initialValues.initial_fedex_accounts]);
        setSelectedFedexAccounts(['all', ...initialValues.initial_fedex_accounts]);
      }
    }
  }, [fedexAccounts, initialValues]);
  useEffect(() => {
    if (formikRef.current && initialValues.user_iwatch_accounts.length > 0 && iWatchAccounts.length > 0) {
      if ((initialValues.user_iwatch_accounts.length === iWatchAccounts.length)) {
        formikRef.current.setFieldValue('selected_iwatch_accounts', ['all', ...initialValues.initial_iwatch_accounts]);
        setSelectedIWatchAccounts(['all', ...initialValues.initial_iwatch_accounts]);
      }
    }
  }, [iWatchAccounts, initialValues]);

  return (
    <Formik
      innerRef={formikRef}
      onSubmit={onSave}
      initialValues={initialValues}
      validate={getFormValidation(user)}
      enableReinitialize
      {...props}
    >
      {({ handleChange, handleBlur, handleSubmit, setFieldValue, errors, values, touched, isSubmitting, dirty }) => {
        const { phone } = values;
        const [ phoneCountry, phoneRemaining ] = splitPhoneNumber(phone);
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            <Grid container spacing={1}>
              <Grid container item xs={12}>
                <InputField
                  name="display_name"
                  label={lm.labels_display_name}
                  value={values.display_name}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  error={touched.display_name && !!errors.display_name}
                />
                <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                  {(touched.display_name && !!errors.display_name) ? errors.display_name : '\xa0'}
                </MKTypography>
              </Grid>
              <Grid container item xs={12}>
                <InputField
                  name="email"
                  label={lm.labels_email}
                  value={values.email}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  disabled={!!user}
                  error={touched.email && !!errors.email}
                />
                <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                  {(touched.email && !!errors.email) ? errors.email : '\xa0'}
                </MKTypography>
              </Grid>
              <Grid container item xs={12}>
                <Select
                  name="phone_country"
                  label="Phone country (Whatsapp)"
                  options={phoneOptions}
                  value={phoneCountry}
                  onChange={(newValue) => {
                    setFieldValue('phone', `${newValue}${phoneRemaining}`);
                  }}
                  handleBlur={handleBlur}
                  error={touched.value && !!errors.value}
                />
              </Grid>
              <Grid container item xs={12}>
                <InputField
                  name="phone"
                  label="Phone (Whatsapp)"
                  value={phone}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  error={touched.phone && !!errors.phone}
                />
                <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                  {(touched.phone && !!errors.phone) ? errors.phone : '\xa0'}
                </MKTypography>
              </Grid>
              {!user && (
                <Grid container item xs={12}>
                  <InputField
                    name="password"
                    label={lm.labels_password}
                    value={values.password}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.password && !!errors.password}
                  />
                  <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                    {(touched.password && !!errors.password) ? errors.password : '\xa0'}
                  </MKTypography>
                </Grid>
              )}
              {!user && (
                <Grid container item xs={12}>
                  <InputField
                    name="confirm_password"
                    label={lm.labels_confirm_password}
                    value={values.confirm_password}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.confirm_password && !!errors.confirm_password}
                  />
                  <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                    {(touched.confirm_password && !!errors.confirm_password) ? errors.confirm_password : '\xa0'}
                  </MKTypography>
                </Grid>
              )}
              {user && (
                <Grid container item xs={12} marginBottom="1rem" alignItems="center">
                  <MKTypography style={{ fontSize: '1rem', marginRight: '2rem' }}>{lm.labels_password}</MKTypography>
                  <Button
                    // variant="gradient"
                    color={setPasswordButtonColor || 'info'}
                    sx={{
                      color: setPasswordFontColor,
                    }}
                    onClick={() => navigate(`set_password?username=${user.email}`)}
                  >
                    {lm.set_password_button}
                  </Button>
                </Grid>
              )}
              <Grid container item xs={12} marginBottom="1rem" alignItems="center" display="flex" flexDirection="row">
                {/* <InputField
                  name="role"
                  label={lm.labels_role}
                  value={values.role}
                  type={INPUT_TYPE_SELECT}
                  setFieldValue={setFieldValue}
                  options={roleOptions}
                  invalid={touched.role && !!errors.role}
                /> */}
                <MKTypography
                  style={{ fontSize: '1rem', marginRight: '2rem' }}
                  color="black"
                  id="role-radio-buttons-group-label"
                >
                  {lm.labels_role}
                </MKTypography>
                <RadioGroup
                  row
                  aria-labelledby="role-radio-buttons-group-label"
                  name="role"
                  value={values.role}
                  error={touched.role && !!errors.role}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {roleOptions && roleOptions.map(roleOption => {
                    const value = roleOption.value;
                    return <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio />}
                      label={roleOption.label}
                    />;
                  })}
                </RadioGroup>
                <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                  {(touched.role && !!errors.role) ? errors.role : '\xa0'}
                </MKTypography>
              </Grid>
              <Grid container item flexDirection="column" xs={12}>
                <Select
                  multiple
                  label="Countries"
                  value={values.selected_countries}
                  onChange={(v) => handleChangeCountry(v, setFieldValue)}
                  options={[{ label: 'All', value: 'all' }, ...countriesOptions]}
                />
              </Grid>
              <Grid container item flexDirection="column" xs={12}>
                <Select
                  multiple
                  label="Fedex Accounts"
                  value={values.selected_fedex_accounts}
                  onChange={(v) => handleChangeFedexAccount(v, setFieldValue)}
                  options={[{ label: 'All', value: 'all' }, ...fedexAccountOptions]}
                />
              </Grid>
              <Grid container item flexDirection="column" xs={12}>
                <Select
                  multiple
                  label="iWatch Accounts"
                  value={values.selected_iwatch_accounts}
                  onChange={(v) => handleChangeIWatchAccount(v, setFieldValue)}
                  options={[{ label: 'All', value: 'all' }, ...iWatchAccountOptions]}
                />
              </Grid>
            </Grid>
            <Grid container mb={5}>
              <Grid item xs={12}>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
                <MKBox display="flex" width="100%" justifyContent="space-between">
                  <Button
                    color={backButtonColor || 'info'}
                    disabled={isSubmitting}
                    style={{
                      border: backButtonColor === 'white' && '1px solid black',
                      width: '20%',
                    }}
                    onClick={() => { navigate(-1); }}
                    sx={{
                      color: backButtonFontColor,
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    color={saveButtonColor || 'info'}
                    disabled={isSubmitting || !dirty}
                    style={{
                      width: '20%',
                    }}
                    sx={{
                      color: saveButtonFontColor,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                    ) : lm.save_button}
                  </Button>
                </MKBox>
              </Grid>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditUserForm.propTypes = {
  user: PropTypes.shape({
    display_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    role: PropTypes.number,
    country_list: PropTypes.string,
    // user_countries: PropTypes.array,
    user_fedex_accounts: PropTypes.array,
    user_iwatch_accounts: PropTypes.array,
  }),
  onSave: PropTypes.func,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      role_id: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  countries: PropTypes.array,
  fedexAccounts: PropTypes.array,
  iWatchAccounts: PropTypes.array,
  setPasswordButtonColor: PropTypes.string,
  setPasswordFontColor: PropTypes.string,
  saveButtonColor: PropTypes.string,
  saveButtonFontColor: PropTypes.string,
  backButtonColor: PropTypes.string,
  backButtonFontColor: PropTypes.string,
};

EditUserForm.defaultProps = {
  user: {},
  onSave: () => {},
  roles: [],
  countries: [],
  fedexAccounts: [],
  iWatchAccounts: [],
};

export default EditUserForm;
